import { FC, useEffect, useState, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/hooks"
import { getTestTypeValue } from "store/slices/test"
import useTimerResume from "hooks/useTimerResume.hook"
import useNestedStep from "hooks/useNestedStep.hook"
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { checkTranslationExists } from "utils/step.utils";

import YouTube from "react-youtube";
import MDButton from "components/MDButton";
import SandClock from "assets/Icons/sandClock.svg?react";

import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import { styles } from "./Step1.styles";
import Timer from "components/Timer/Timer";

import { log } from "services/logger";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";

/** Stable id for Step1 nested-step persistence (independent of wizard order) */
const PARENT_STEP = 'step1';
const TIMER_LENGTH = 120;
interface StepProps {
  handleUpdateStep: (step: number) => void;
}

type Step1Map = {
  [key: number]: {
    Component: ReactElement;
    nextButtonText: string;
  };
};

const InnerStep1: FC<any> = ({ classes, youTubeOptions, onReady }) => {
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);

  // Dynamically find all text items
  const textItems = useMemo(() => {
    const items: string[] = [];
    const baseKey = `${testTypeLocaleKey}:Step1Screen:text`;
    let i = 1;
    while (checkTranslationExists(baseKey, i, t)) {
        items.push(t(`${baseKey}${i}`));
      i++;
    }
    return items;
  }, [t, testTypeLocaleKey]);
  const onVideoPlayedHandler =  (event: any) => {
    event.target.playVideo();
    
    // Log tutorial video viewed event
    sentryLogMessage({
      message: 'Step1 Tutorial video viewed',
      level: 'info',
      tags: {
        component: 'Step1',
        category: 'tutorial_video',
        event_type: SENTRY_EVENT_TYPES.VIEWED_TUTORIAL_VIDEO,
      },
      extra:{
        videoId: t(`${testTypeLocaleKey}:Step1Screen:youtubeVideo`),
        timestamp: new Date().toISOString(),
      }
    });
  };

  return (
    <>
      {textItems.map((text, index) => (
        <div key={index} className={classes.text}>
          <div className={classes.textBullet}>{index + 1}.&nbsp;&nbsp;</div>
          <span>{text}</span>
        </div>
      ))}
      <div className={classes.youTubeContainer}>
        {youTubeOptions ? (
          <YouTube
            iframeClassName={classes.iframeClassName}
            videoId={t(`${testTypeLocaleKey}:Step1Screen:youtubeVideo`)}
            opts={youTubeOptions}
            onReady={onReady}
            onPlay={onVideoPlayedHandler}
          />
        ) : null}
      </div>
    </>
  );
};
const InnerStep2: FC<any> = ({ classes, setTimerDone }) => {
  const { t } = useTranslation()
  const testType = useAppSelector(getTestTypeValue)
  const testTypeLocaleKey = getTestTypeLocaleKey(testType)
  const { sessionId, timerId } = useTimerResume('step1Timer')

  return (
    <div className={classes.innerStep2Container}>
      <div className={classes.sandClockContainer}>
        <SandClock />
      </div>
      <div className={classes.timerText1}>{t(`${testTypeLocaleKey}:Step1Screen:timerText1`)}</div>
      <Timer
        timerLength={TIMER_LENGTH}
        sessionId={sessionId}
        timerId={timerId}
        setTimerDone={setTimerDone}
      />
    </div>
  )
}

const Step1: FC<StepProps> = ({ handleUpdateStep }) => {
  const [step1InnerStep, setStep1InnerStep] = useNestedStep(PARENT_STEP)

  const [youTubeOptions, setYouTubeOptions] = useState()
  const [timerDone, setTimerDone] = useState(false)
  const { t } = useTranslation()
  const testType = useAppSelector(getTestTypeValue)
  const testTypeLocaleKey = getTestTypeLocaleKey(testType)

  useEffect(() => {
    log('Hello app', {welcome: "to app"})
  }, [])
  
  const theme = useTheme()
  const useStyles = makeStyles()(() => styles(theme))
  const { classes } = useStyles()

  const onReady = (event: any) => {
    event.target.pauseVideo()
  }

  useEffect(() => {
    const container = document.getElementById("Step1Container")
    if (!container) {
      // Retry after a short delay
      const timeoutId = setTimeout(() => {
        const retryContainer = document.getElementById("Step1Container")
        if (retryContainer) {
          const youTubePlayerWidth = retryContainer.clientWidth
          const options: any = {
            height: 216,
            width: youTubePlayerWidth,
          }
          setYouTubeOptions(options)
        } else {
          console.error("[Step1] Container still not found after retry")
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
    
    const youTubePlayerWidth = container.clientWidth
    const options: any = {
      height: 216,
      width: youTubePlayerWidth,
    }
    setYouTubeOptions(options)
  }, [])

  const body: Step1Map = useMemo(() => {
    return {
      1: {
        Component: (
          <InnerStep1
            classes={classes}
            youTubeOptions={youTubeOptions}
            onReady={onReady}
          />
        ),
        nextButtonText: t(`${testTypeLocaleKey}:Step1Screen:nextButton1`),
      },
      2: {
        Component: <InnerStep2 classes={classes} setTimerDone={setTimerDone} />,
        nextButtonText: t(`${testTypeLocaleKey}:Step1Screen:nextButton2`),
      },
    }
  }, [classes, youTubeOptions, onReady, testTypeLocaleKey, t, setTimerDone])

  const handleNext = () => {
    if (step1InnerStep === 1) {
      // start timer
      setStep1InnerStep(2)
    }
    if (step1InnerStep === 2) {
      handleUpdateStep(3)
    }
  }

  return (
    <div id="Step1Container" className={classes.container}>
      <div className={classes.bodyContainer}>
        {body[step1InnerStep].Component}
      </div>

      <MDButton
        style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
        color={"primary"}
        variant="contained"
        type="submit"
        onClick={handleNext}
        disabled={step1InnerStep === 2 && !timerDone}
      >
        {body[step1InnerStep].nextButtonText}
      </MDButton>
    </div>
  )
}

export default Step1
